/**
 * analytics.js — web-only conversion tracking, inert until IDs are configured.
 *
 * Loads GA4 / Meta Pixel / Microsoft UET / LinkedIn Insight ONLY when the
 * matching Vite env var is set, and ONLY on the web app — never in the Chrome
 * extension side panel (strict CSP, must ship no remote code). `track()` no-ops
 * until initAnalytics() has run, so the shared AIMail component can call it
 * safely in both contexts.
 *
 * Configure via frontend/.env (build-time; all optional):
 *   VITE_GA4_ID=G-XXXXXXXXXX
 *   VITE_META_PIXEL_ID=000000000000000
 *   VITE_BING_UET_ID=00000000
 *   VITE_LINKEDIN_PARTNER_ID=0000000
 */
import { emitProductEvent } from './productEvents.js';

const env = (typeof import.meta !== 'undefined' && import.meta.env) || {};
const GA4 = env.VITE_GA4_ID;
const META_PIXEL = env.VITE_META_PIXEL_ID;
const UET = env.VITE_BING_UET_ID;
const LINKEDIN = env.VITE_LINKEDIN_PARTNER_ID;
const API_BASE = env.VITE_API_BASE || '';

let initialized = false;
let utm = {};

// Stable per-device id so the backend sink can attribute extension-side events
// in GA4 (the Measurement Protocol requires a client_id).
function getClientId() {
  try {
    let id = localStorage.getItem('persona_client_id');
    if (!id) {
      id = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}.${Math.floor(Math.random() * 1e9)}`;
      localStorage.setItem('persona_client_id', id);
    }
    return id;
  } catch { return 'anon'; }
}

const isExtension = () => typeof window !== 'undefined' && !!window.__AIMAIL_IS_EXTENSION;

/** Capture utm_* params on landing → persist so they survive to the conversion. */
export function captureUTM() {
  try {
    const p = new URLSearchParams(window.location.search);
    const found = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((k) => {
      const v = p.get(k);
      if (v) found[k] = v;
    });
    if (Object.keys(found).length) localStorage.setItem('persona_utm', JSON.stringify(found));
    utm = JSON.parse(localStorage.getItem('persona_utm') || '{}');
  } catch {
    utm = {};
  }
  return utm;
}

/** Initialise enabled vendors. Web-only; safe to call once on app mount. */
export function initAnalytics() {
  if (initialized || isExtension() || typeof window === 'undefined') return;
  initialized = true;
  captureUTM();

  // ── GA4 ──
  if (GA4) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', GA4, { send_page_view: true });
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4}`;
    document.head.appendChild(s);
  }

  // ── Meta Pixel ──
  if (META_PIXEL) {
    /* eslint-disable */
    !function (f, b, e, v, n, t, s) {
      if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments) };
      if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
      t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s)
    }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */
    window.fbq('init', META_PIXEL);
    window.fbq('track', 'PageView');
  }

  // ── Microsoft UET (Bing) ──
  if (UET) {
    /* eslint-disable */
    (function (w, d, t, r, u) {
      var f, n, i; w[u] = w[u] || [], f = function () { var o = { ti: UET, enableAutoSpaTracking: true }; o.q = w[u], w[u] = new w.UET(o), w[u].push('pageLoad') },
        n = d.createElement(t), n.src = r, n.async = 1, n.onload = n.onreadystatechange = function () { var s = this.readyState; s && s !== 'loaded' && s !== 'complete' || (f(), n.onload = n.onreadystatechange = null) },
        i = d.getElementsByTagName(t)[0], i.parentNode.insertBefore(n, i)
    })(window, document, 'script', 'https://bat.bing.com/bat.js', 'uetq');
    /* eslint-enable */
  }

  // ── LinkedIn Insight (retargeting + base pageview; conversions set in-platform) ──
  if (LINKEDIN) {
    window._linkedin_partner_id = String(LINKEDIN);
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(String(LINKEDIN));
    /* eslint-disable */
    (function (l) {
      if (!l) { window.lintrk = function (a, b) { window.lintrk.q.push([a, b]) }; window.lintrk.q = [] }
      var s = document.getElementsByTagName('script')[0]; var b = document.createElement('script');
      b.type = 'text/javascript'; b.async = true; b.src = 'https://snap.licdn.com/li.lms-analytics/insight.min.js';
      s.parentNode.insertBefore(b, s)
    })(window.lintrk);
    /* eslint-enable */
  }
}

// Event-name maps per vendor (GA4 uses our names; Meta uses standard events).
const FB_EVENT = { sign_up: 'CompleteRegistration', email_generated: 'Lead', purchase: 'Purchase' };

/**
 * Forward one event to the backend sink.
 *
 * Two jobs, depending on `source`:
 *   extension — the side panel can't load GA4/pixels (CSP + no-remote-code), so
 *     the backend relays to GA4 server-side AND captures to PostHog.
 *   web — gtag/fbq already fired in this browser, so the backend skips GA4 and
 *     only captures to PostHog. Without this call the web half of every funnel
 *     would be missing from product analytics.
 */
function beacon(event, params, source) {
  const send = (token) => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      // Optional. The backend resolves user_id from this token and NEVER from
      // the body — that is what lets sign_up alias the anonymous client to the
      // person in PostHog, and it is why the id can't be spoofed.
      if (token) headers['Authorization'] = `Bearer ${token}`;
      fetch(`${API_BASE}/api/v1/event`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ client_id: getClientId(), event, params, source }),
        keepalive: true,
      }).catch(() => {});
    } catch {}
  };
  // Resolve the token opportunistically; a signed-out user just sends none.
  // Dynamic import so the analytics module has no load-order dependency on the
  // Supabase client, which initialises later.
  try {
    import('./supabase.js')
      .then((m) => m.getAccessToken())
      .then(send)
      .catch(() => send(null));
  } catch { send(null); }
}

/** Fire a conversion/engagement event across all enabled vendors. No-op until init. */
export function track(event, params = {}) {
  if (isExtension()) {
    beacon(event, params, 'extension');
    return;
  }
  if (!initialized) return;
  const merged = { ...utm, ...params };
  try { if (GA4 && window.gtag) window.gtag('event', event, merged); } catch {}
  try { if (META_PIXEL && window.fbq) window.fbq('track', FB_EVENT[event] || event, params); } catch {}
  try { if (UET && window.uetq) window.uetq.push('event', event, params); } catch {}
  // PostHog has no browser tag here on purpose (see backend/posthog_sink.py) —
  // the same bundle ships inside the extension, where a remote SDK is forbidden.
  beacon(event, merged, 'web');
}

/** The UTM set captured on landing, for callers that need to attach it. */
export function getUTM() {
  try { return { ...(utm || {}) }; } catch { return {}; }
}

/**
 * Fire `sign_up` at most once per browser. Supabase's SIGNED_IN event fires for
 * both new registrations and returning logins, so we guard with a flag to
 * approximate first registration (acceptable over-count on a cleared browser).
 */
export function trackSignupOnce(userId) {
  try {
    if (localStorage.getItem('persona_signup_tracked')) return;
    localStorage.setItem('persona_signup_tracked', userId || '1');
    const attribution = getUTM();
    track('sign_up', attribution);
    // Also write it to product_events. sign_up is a funnel DENOMINATOR — the
    // activated→paid and wall→paid rates divide by it — so it has to live in the
    // same client_id space as install and draft_landed, not only in GA4.
    // Carrying the UTM set here is what makes CAC-by-channel answerable at all:
    // the Chrome Web Store strips click attribution, so this is the only point
    // where channel and user meet (§2.0 attribution reality).
    emitProductEvent('sign_up', { extra: attribution });
  } catch {}
}
